import { createId } from './ids';

/**
 * A pattern is a tile of straight-alpha RGBA rows, top row first, exactly like a
 * layer surface. Tiles live outside the React store for the same reason sampled
 * brush tips do: they are byte arrays nothing in the UI needs to diff.
 */
export interface PatternImage {
  id: string;
  name: string;
  width: number;
  height: number;
  data: Uint8Array;
}

const patterns = new Map<string, PatternImage>();
let epoch = 0;

for (const pattern of createBuiltinPatterns()) patterns.set(pattern.id, pattern);

export function registerPattern(pattern: PatternImage): void {
  patterns.set(pattern.id, pattern);
  epoch += 1;
}

export function getPattern(id: string | null | undefined): PatternImage | null {
  if (!id) return null;
  return patterns.get(id) ?? null;
}

export function allPatterns(): PatternImage[] {
  return [...patterns.values()];
}

/** Bumped on every new tile, so React and the texture cache can notice. */
export function patternEpoch(): number {
  return epoch;
}

function solid(
  name: string,
  size: number,
  paint: (x: number, y: number) => [number, number, number, number],
): PatternImage {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const [r, g, b, a] = paint(x, y);
      const at = (y * size + x) * 4;
      data[at] = r;
      data[at + 1] = g;
      data[at + 2] = b;
      data[at + 3] = a;
    }
  }
  return { id: createId('pattern'), name, width: size, height: size, data };
}

/** A checker and two line tiles, enough to have something in the picker. */
function createBuiltinPatterns(): PatternImage[] {
  return [
    solid('Checkerboard', 16, (x, y) => {
      const light = (x < 8) === (y < 8);
      const value = light ? 220 : 160;
      return [value, value, value, 255];
    }),
    solid('Diagonal Lines', 8, (x, y) => {
      const on = (x + y) % 8 < 4;
      const value = on ? 235 : 120;
      return [value, value, value, 255];
    }),
    solid('Dots', 12, (x, y) => {
      const dx = x - 6;
      const dy = y - 6;
      const inside = dx * dx + dy * dy <= 9;
      return inside ? [70, 70, 75, 255] : [230, 230, 235, 255];
    }),
  ];
}
